/**
 * Integration Service for backend API communication
 *
 * SECURITY UPDATE: April 2025
 * All direct references to backend URLs and credentials have been replaced with secure server proxies
 * to prevent exposing sensitive information in client-side code.
 */
export const useIntegrateService = () => {
  const dayjs = useDayjs();

  /**
   * Get authentication token from secure server proxy
   * This method no longer exposes credentials in client-side code
   */
  const getToken = async (): Promise<string> => {
    // Use proxy endpoint to avoid CORS issues and maintain security
    // This approach avoids exposing credentials in client code - no credentials exposed
    const loginUrl = `/api/integrate/goPrepaid/auth`;

    try {
      // Use the secure proxy endpoint for authentication
      const loginResponse: any = await $fetch(loginUrl, {
        method: "GET",
      });

      // Check if the response contains an error
      if (loginResponse.error || !loginResponse.authToken) {
        console.error(
          "Auth Error:",
          loginResponse.error || "No auth token returned"
        );
        throw new Error(loginResponse.error || "Authentication failed");
      }

      // Verify the token exists - support both response formats
      // This provides better compatibility with both API response structures
      if (loginResponse.token) {
        return loginResponse.token;
      } else if (loginResponse.authToken?.access_token) {
        return loginResponse.authToken.access_token;
      } else {
        console.error("Invalid token format in response:", loginResponse);
        throw new Error("Invalid token format in response");
      }
    } catch (error) {
      console.error("Error getting auth token:", error);
      throw error;
    }
  };

  /**
   * Submit a simple order via secure server proxy
   */
  const submitSimpleOrder = async (payload: any): Promise<any | null> => {
    try {
      // Use the correct proxy endpoint path - no hardcoded goPrepaid to prevent duplication
      const endpoint = `/api/integrate/simpleOrder`;
      const response: any = await $fetch(endpoint, {
        method: "POST",
        body: payload,
      });
      return response.result;
    } catch (error: any) {
      console.log("Error submitting simple order:", error);
      // Pass through the original error without message extraction
      // This allows the calling component to handle error extraction consistently
      throw error;
    }
  };

  /**
   * Search addresses via secure server proxy
   */
  const searchAddresses = async (payload: object): Promise<any | null> => {
    try {
      // FIXED: Removed hardcoded goPrepaid segment to prevent duplicate path segments
      const response: any = await $fetch(
        "/api/integrate/address/autoComplete",
        {
          method: "POST",
          body: payload,
        }
      );

      // Check for errors in the response
      if (response?.error) {
        console.error("Address search error:", response.error);
        return null;
      }

      const data = response?.result || null;
      return data;
    } catch (error) {
      console.error("Error searching addresses:", error);
      return null; // Return null rather than throwing to prevent cascading errors
    }
  };

  /**
   * Get countries via secure server proxy
   */
  const getCountries = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/countries`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Get postcode metadata via secure server proxy
   */
  const getPostCodeMetadata = async (payload: {
    country: string;
    postcode: string;
  }): Promise<any | null> => {
    try {
      // Use the correct endpoint path for postcode metadata
      const response: any = await $fetch(
        "/api/integrate/goPrepaid/address/getPostcodeMetadata",
        {
          method: "GET",
          params: payload,
        }
      );

      const data = response?.result || null;
      return data;
    } catch (error) {
      console.error("Error retrieving postcode metadata", error);
      return null;
    }
  };

  const getProducts = async (): Promise<any | null> => {
    try {
      const dailyDateRange = [
        dayjs().startOf("day").format(), // "2024-01-01T00:00:00Z",
        dayjs().endOf("day").format(), //  "2024-01-01T00:00:00Z",
      ];
      const payload = {
        filters: {
          inListchannels: "GO_COMMERCE",
          inListofferTemplateCategories: "COLES,COLES_M2M,COLES_PREPAID",
          lifeCycleStatus: {
            value: "ACTIVE",
          },
          disabled: {
            value: false,
          },
          "overlapOptionalRange validity.from validity.to": dailyDateRange,
        },
        fields:
          "offerDiscountPlans,offerServiceTemplates.serviceTemplate.serviceChargeTemplateRecurrings,offerServiceTemplates.serviceTemplate.serviceChargeTemplateSubscriptions,offerServiceTemplates.serviceTemplate.serviceChargeTemplateUsages,offerServiceTemplates.serviceTemplate.serviceChargeTemplateTerminations",
        offset: "0",
        limit: "100",
        sortBy: "id",
        sortOrder: "ASCENDING",
        loadReferenceDepth: 100,
      };

      // FIXED: Remove duplicate goPrepaid segment in path
      // The universal proxy already handles the proper routing to /webhook/goPrepaid/products
      const response: any = await $fetch(`/api/integrate/products`, {
        method: "POST",
        body: payload,
      });
      const data = response?.result || null;
      return data;
    } catch (error: any) {
      return false;
    }
  };
  /**
   * Get selected destinations via secure server proxy
   */
  const getSelectedDestinations = async (): Promise<any | null> => {
    try {
      // Use the correct endpoint path for selected destinations without duplicating goPrepaid
      const response: any = await $fetch(
        "/api/integrate/selected-destinations",
        {
          method: "GET",
        }
      );

      const data = response || null;
      return data;
    } catch (error) {
      console.error("Error retrieving selected destinations", error);
      return null;
    }
  };
  /**
   * Enable or disable auto-recharge for a customer service via secure server proxy
   * Using the dedicated endpoint with correct backend method handling (PATCH)
   */
  const autoRecharge = async (payload: any): Promise<any | null> => {
    try {
      console.log("Calling auto-recharge function with payload:", payload);

      // Use the fixed auto-recharge endpoint that sends PATCH requests to backend
      const response: any = await $fetch("/api/integration/auto-recharge", {
        method: "POST",
        body: payload, // Send the original payload - server proxy will use PATCH to backend
      });

      return response || null;
    } catch (error) {
      console.error("Error updating auto-recharge settings:", error);
      throw error;
    }
  };

  return {
    searchAddresses,
    getCountries,
    getPostCodeMetadata,
    getProducts,
    getSelectedDestinations,
    submitSimpleOrder,
    autoRecharge, // Add the new function to the return object
  };
};
